import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import classes from './UiLink.module.css';

export interface UiLinkProps {
  /** `#id` scrolls to an element on this page, `http(s)://` opens outside, anything else is an app path without language prefix. */
  to: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}

export function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  if (el.tabIndex < 0 && !el.hasAttribute('tabindex')) {
    el.setAttribute('tabindex', '-1');
  }
  el.focus({ preventScroll: true });
}

export function UiLink({ to, children, className, onClick }: UiLinkProps) {
  const { createLanguageURL } = useLanguageAwareRouting();
  const cls = className ? `${classes.link} ${className}` : classes.link;

  if (to.startsWith('#')) {
    // A plain href="#id" would replace the hash route.
    const onAnchorClick = (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      scrollToSection(to.slice(1));
      onClick?.();
    };
    return (
      <a href={to} className={cls} onClick={onAnchorClick}>
        {children}
      </a>
    );
  }

  if (/^https?:\/\//.test(to)) {
    return (
      <a href={to} className={cls} onClick={onClick} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }

  return (
    <Link to={createLanguageURL(to)} className={cls} onClick={onClick}>
      {children}
    </Link>
  );
}
